import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type { RecentRecord, RecordVisitRequest } from '../types/recent.types';

/**
 * How many rows a module panel asks for, and therefore how many the local cache keeps when a visit
 * is spliced in. One constant so the optimistic list can never be longer than a fetched one.
 */
export const RECENT_TAKE = 5;

/**
 * The caller's "recently viewed" list (M4 · 86d44z223).
 *
 * Both calls are marked `background: true`: neither is something the user asked for, so neither may
 * ever surface a toast. Recent is a convenience — if it fails, the panel simply shows no Recent
 * section, and the page the user actually wanted is unaffected.
 */
class RecentService {
  /** Newest first, already permission-filtered and existence-checked server-side. */
  async getForModule(moduleId: string, take = RECENT_TAKE): Promise<RecentRecord[]> {
    const { data } = await apiClient.get<ApiResponse<RecentRecord[]>>('/recent', {
      params: { moduleId, take },
      background: true,
    });
    return data.data ?? [];
  }

  /**
   * "I just opened this record."
   *
   * ⚠️ STILL FIRE AND FORGET. The caller must not await this in a way that can delay or fail a page
   * render — bookkeeping never blocks the thing the user asked for. Rejections are swallowed here,
   * resolving to `null`, so a caller cannot accidentally create an unhandled rejection.
   *
   * ⚠️ It RESOLVES WITH THE ROW, which is the whole point: the Recent list only ever changes because
   * of an action this client just took, so the server hands back what it wrote and the caller
   * splices it in. Without this the panel showed a stale list until it was reopened or the page was
   * reloaded — the request was already being made, and its answer thrown away.
   */
  async recordVisit(request: RecordVisitRequest): Promise<RecentRecord | null> {
    try {
      const { data } = await apiClient.post<ApiResponse<RecentRecord | null>>('/recent', request, {
        background: true,
      });
      return data.data ?? null;
    } catch {
      /* A lost Recent entry is not worth an error. Deliberately silent. */
      return null;
    }
  }
}

export const recentService = new RecentService();
